<template>
  <UIDrawer
    :title
    :is-open
    :z-index
    :allow-tray
    @minimize="handleMinimize"
    @close="handleClose"
  >
    <template v-if="!isNewTransaction" #actions>
      <UITrashCan @click="handleDelete" />
    </template>
    <TransactionForm v-bind="$attrs" :instance @close="handleClose" />
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, markRaw } from 'vue'

import { TransactionClass } from '..'
import { ModalClass } from '@types'

import {
  TRANSACTION_MODAL_TITLE,
  TRANSACTION_MODAL_TITLE_NEW,
} from './utils/const'

import { useModalsStore } from '@/store/modals'
import { useTransactionsDeletedStore } from '@/store/transactions/deleted'

import { UITrashCan } from '@ui/core'
import { UIDialogRemove, UIDrawer } from '@ui/modals'
import TransactionForm from './TransactionForm.vue'

type Props = {
  modal: ModalClass<TransactionClass>
  title?: string
}

const props = defineProps<Props>()

const modalsStore = useModalsStore()
const transactionsDeletedStore = useTransactionsDeletedStore()

const instance = computed(() => props.modal.entity)
const isNewTransaction = computed(() => instance.value.isNew)

const title = computed(
  () =>
    props.title ||
    (isNewTransaction.value
      ? TRANSACTION_MODAL_TITLE_NEW
      : TRANSACTION_MODAL_TITLE),
)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)
const allowTray = computed(() => props.modal.allowTray)

const handleMinimize = () => {
  props.modal.hide()
}

const handleClose = () => {
  props.modal.conditionalClose()
}

const handleDelete = () => {
  const modalInstance = modalsStore.init('transaction-delete-confirm', markRaw(UIDialogRemove))

  modalInstance?.open(modalsStore.getZIndex(), {
    title: 'Delete transaction',
    message: 'Are you sure you want to delete this transaction?',
    label: 'Delete',
    onRemove: async () => {
      await instance.value.destroy()
      handleClose()
      await transactionsDeletedStore.fetch()
    }
  })
}
</script>
